import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useThemeStore } from './store/theme';
import { CookieConsent } from './components/CookieConsent';
import { AppShell } from './app/AppShell';
import { RequireRole } from './app/RequireRole';
import clsx from 'clsx';
import { useEffect, lazy, Suspense } from 'react';

// Lazy load components
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const WorkspaceHome = lazy(() => import('./pages/WorkspaceHome'));
const BuilderPage = lazy(() => import('./pages/BuilderPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));

// Loading component
const Loading = () => (
  <div className="flex min-h-screen items-center justify-center bg-surface">
    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-accent" />
  </div>
);

const THEME_STORAGE_KEY = 'theme-storage';

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<AppShell />}>
        <Route index element={<WorkspaceHome />} />
        <Route path="f/:flowId" element={<BuilderPage />} />
        <Route
          path="admin"
          element={
            <RequireRole role="admin">
              <AdminPanel />
            </RequireRole>
          }
        />
      </Route>
      {/* legacy redirects */}
      <Route path="/flow" element={<Navigate to="/app" replace />} />
      <Route path="/mainApp" element={<Navigate to="/app" replace />} />
      <Route path="/push" element={<Navigate to="/app" replace />} />
      <Route path="/waitingListNotice" element={<Navigate to="/app" replace />} />
      <Route path="/accessDenied" element={<Navigate to="/app" replace />} />
      <Route path="/adminAccess" element={<Navigate to="/app/admin" replace />} />
    </Routes>
  );
}

function App() {
  const isDarkMode = useThemeStore((state) => state.isDarkMode);
  const setDarkMode = useThemeStore((state) => state.setDarkMode);

  useEffect(() => {
    // Seed the theme from the OS preference exactly once, and only when no
    // persisted user choice exists yet. The theme store's own `persist`
    // middleware owns the value from then on — this is intentionally not an
    // ongoing 'change' listener, since that would silently override a
    // user's manual toggle whenever their OS theme changes later.
    if (localStorage.getItem(THEME_STORAGE_KEY) !== null) return;
    setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
  }, [setDarkMode]);

  useEffect(() => {
    // `bg-surface`/`text-body`/etc. are CSS custom properties that only
    // flip to their dark values underneath a `.dark` ancestor — the
    // wrapper div below carries that class for everything React renders,
    // but <html>/<body> themselves sit OUTSIDE it in the DOM. index.css's
    // `body { @apply bg-surface }` base rule then always resolves against
    // the light :root value, so any area where body's own background is
    // what's actually painted (elastic overscroll bounce past the top/
    // bottom, scrollbar gutters) flashes light even in dark mode. Mirroring
    // the class onto <html> too fixes that for the whole app, not just one
    // page, since <body> is a strict descendant of <html>.
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  return (
    <BrowserRouter>
      <div className={clsx('min-h-screen', isDarkMode && 'dark')}>
        <Suspense fallback={<Loading />}>
          <AppRoutes />
        </Suspense>
        <CookieConsent />
      </div>
    </BrowserRouter>
  );
}

export default App;
